import {
  DateTime,
} from 'luxon';

import {
  getLuxonDate,
} from '@/composables/date.js';

import {
  GLOBAL_CONSTANTS,
} from '@/constants';

import {
  conference_repository,
  search_repository,
} from '@/repositories';

const state = () => ({
  conference: null,
  conference_list: [],
  criteria_conference_publication_date_lower_boundary: null,
  criteria_limit: GLOBAL_CONSTANTS.CONFERENCES_LIMIT,
  criteria_new_conferences: false,
  criteria_offset: 0,
  criteria_quick_search: '',
  criteria_sort: 'start_date',
  criteria_start_date_lower_boundary: DateTime
    .utc()
    .toISO(),
  criteria_start_date_upper_boundary: null,
  criteria_theme_list_id: [],
  facet_new_conferences_count: 0,
  facet_theme_count: 0,
  loading_search_conferences_and_facets: false,
  reach_end_search_conferences_and_facets: false,
});

const getters = {
  conference: (state) => state.conference,
  conference_list: (state) => state.conference_list,
  criteria_conference_publication_date_lower_boundary: (state) => state
    .criteria_conference_publication_date_lower_boundary,
  criteria_limit: (state) => state.criteria_limit,
  criteria_new_conferences: (state) => state.criteria_new_conferences,
  criteria_offset: (state) => state.criteria_offset,
  criteria_quick_search: (state) => state.criteria_quick_search,
  criteria_sort: (state) => state.criteria_sort,
  criteria_start_date_lower_boundary: (state) => state.criteria_start_date_lower_boundary,
  criteria_start_date_upper_boundary: (state) => state.criteria_start_date_upper_boundary,
  criteria_theme_list_id: (state) => state.criteria_theme_list_id,
  facet_new_conferences_count: (state) => state.facet_new_conferences_count,
  facet_theme_count: (state) => state.facet_theme_count,
  loading_search_conferences_and_facets: (state) => state.loading_search_conferences_and_facets,
  reach_end_search_conferences_and_facets: (state) => state.reach_end_search_conferences_and_facets,
};

const actions = {
  async getConferenceById({ commit }, conference_id) {

    const response = await conference_repository.searchConferences({
      conference_id_list: [conference_id],
      limit: 1,
    });

    const [conference] = response.news_list;

    if (conference === undefined) {
      commit('set_conference', null);

      return null;
    }

    if (conference.highlight_list !== null) {
      await Promise.all(
        conference.highlight_list.map((highlight) => search_repository
          .getContactsActive({
            job_highlight_id_list: [highlight.id],
            limit: 100,
          })
          .then(({contact_list}) => {
            highlight.contact_list = contact_list.map((contact) => ({
              ...contact,
              main_job: contact.job_list.find((job) => job.active && job.main),
            }));
          })
          .catch(() => {
            highlight.contact_list = [];
          })
        )
      );

      conference.highlight_list = conference.highlight_list.sort((a, b) => {
        if (a.start_date < b.start_date) {
          return -1;
        }

        if (a.start_date > b.start_date) {
          return 1;
        }

        return 0;
      });
    }

    commit('set_conference', conference);

    return conference;
  },
  async searchConferencesAndFacets({ commit, rootState, state }) {
    commit('set_loading_search_conferences_and_facets', true);

    if (state.criteria_offset === 0) {
      commit('set_conference_list', []);
    }

    const data = {
      limit: state.criteria_limit,
      start_date_lower_boundary: getLuxonDate(state.criteria_start_date_lower_boundary, {zone: 'utc'})
        .startOf('day')
        .toISO(),
      offset: state.criteria_offset,
      sort: [state.criteria_sort],
    };

    if (state.criteria_quick_search) {
      data.quick_search = [state.criteria_quick_search];
    }

    if (state.criteria_new_conferences) {
      data.conference_publication_date_lower_boundary = (rootState.user.token_data.last_visit_at)
        ? getLuxonDate(rootState.user.token_data.last_visit_at, {zone: 'utc'})
        : getLuxonDate(rootState.user.token_data.connected_at, {zone: 'utc'});
    }

    if (state.criteria_start_date_upper_boundary) {
      data.start_date_upper_boundary = getLuxonDate(state.criteria_start_date_upper_boundary, {zone: 'utc'})
        .endOf('day')
        .toISO();
    }

    if (state.criteria_theme_list_id.length > 0) {
      data.theme_list_id = state.criteria_theme_list_id;
    }

    const promise_list = [
      conference_repository.searchConferences(data),
    ];

    if (state.criteria_offset === 0) {
      const data_facet_new_conferences = {
        ...data,
        conference_publication_date_lower_boundary: (rootState.user.token_data.last_visit_at)
          ? getLuxonDate(rootState.user.token_data.last_visit_at, {zone: 'utc'})
          : getLuxonDate(rootState.user.token_data.connected_at, {zone: 'utc'}),
        limit: 0,
        offset: 0,
      };

      promise_list.push(conference_repository.searchConferences(data_facet_new_conferences));
    }

    try {
      const [
        response_conferences,
        response_facet_new_conferences,
      ] = await Promise.all(promise_list);

      let conference_list;

      if (state.criteria_offset === 0) {
        conference_list = response_conferences.news_list;

        commit('set_facet_theme_count', response_conferences.news_count);
        commit('set_facet_new_conferences_count', response_facet_new_conferences.news_count);
      } else {
        conference_list = state.conference_list.concat(response_conferences.news_list);
      }

      if (response_conferences.news_list.length < state.criteria_limit) {
        commit('set_reach_end_search_conferences_and_facets', true);
      }

      commit('set_criteria_offset', state.criteria_offset + GLOBAL_CONSTANTS.CONFERENCES_LIMIT);
      commit('set_conference_list', conference_list);
      commit('set_loading_search_conferences_and_facets', false);
    } catch (error) {
      console.error('searchConferencesAndFacets_error', error);

      commit('set_loading_search_conferences_and_facets', false);
    }

    return;
  },
};

const mutations = {
  set_conference(state, conference) {
    state.conference = conference;
  },
  set_conference_list(state, conference_list) {
    state.conference_list = conference_list;
  },
  set_criteria_conference_publication_date_lower_boundary(
    state, criteria_conference_publication_date_lower_boundary
  ) {
    // eslint-disable-next-line vue/max-len
    state.criteria_conference_publication_date_lower_boundary = criteria_conference_publication_date_lower_boundary;
  },
  set_criteria_sort(state, criteria_sort) {
    state.criteria_sort = criteria_sort;
  },
  set_criteria_limit(state, criteria_limit) {
    state.criteria_limit = criteria_limit;
  },
  set_criteria_new_conferences(state, criteria_new_conferences) {
    state.criteria_new_conferences = criteria_new_conferences;
  },
  set_criteria_offset(state, criteria_offset) {
    state.criteria_offset = criteria_offset;
  },
  set_criteria_quick_search(state, criteria_quick_search) {
    state.criteria_quick_search = criteria_quick_search;
  },
  set_criteria_start_date_lower_boundary(state, criteria_start_date_lower_boundary) {
    state.criteria_start_date_lower_boundary = criteria_start_date_lower_boundary;
  },
  set_criteria_start_date_upper_boundary(state, criteria_start_date_upper_boundary) {
    state.criteria_start_date_upper_boundary = criteria_start_date_upper_boundary;
  },
  set_criteria_theme_list_id(state, criteria_theme_list_id) {
    state.criteria_theme_list_id = criteria_theme_list_id;
  },
  set_facet_new_conferences_count(state, facet_new_conferences_count) {
    state.facet_new_conferences_count = facet_new_conferences_count;
  },
  set_facet_theme_count(state, facet_theme_count) {
    state.facet_theme_count = facet_theme_count;
  },
  set_loading_search_conferences_and_facets(state, loading_search_conferences_and_facets) {
    state.loading_search_conferences_and_facets = loading_search_conferences_and_facets;
  },
  set_reach_end_search_conferences_and_facets(state, reach_end_search_conferences_and_facets) {
    state.reach_end_search_conferences_and_facets = reach_end_search_conferences_and_facets;
  },
};

export default {
  actions,
  getters,
  mutations,
  namespaced: true,
  state,
};
